import { formatDateTime } from "@/api/datetime";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import type { AgencyModel } from "@/firestore_types";
import { formatPhoneNumber } from "@/lib/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import { Timestamp } from "firebase/firestore";
import { ArrowUpDown, Eye, Plus } from "lucide-react";
import { debounce, parseAsString, useQueryState } from "nuqs";
import { useMemo } from "react";
import { useAdminAgencies } from "../-admin-context";

const formatDate = (timestamp: Date | Timestamp | undefined | null) => {
  if (!timestamp) return "N/A";
  if (timestamp instanceof Date) {
    return formatDateTime(timestamp);
  } else if (timestamp instanceof Timestamp) {
    return formatDateTime(timestamp.toDate());
  }
  return "N/A";
};

export const Route = createFileRoute("/dashboard/admin/agencies/")({
  head: () => ({
    meta: [
      {
        title: "Agencies - GetHandle Admin",
      },
    ],
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const agenciesResource = useAdminAgencies();
  const [search, setSearch] = useQueryState("search", parseAsString.withDefault(""));

  const handleSearchChange = (value: string) => {
    setSearch(value, {
      limitUrlUpdates: value === "" ? undefined : debounce(300)
    });
  };

  // DataTable columns
  const columns = useMemo<ColumnDef<AgencyModel>[]>(() => [
    // Hidden helper column for multi-field search
    {
      id: "all",
      accessorFn: (row) => {
        const parts = [
          row.name,
          ...(row.emails ?? []),
          ...(row.phoneNumbers ?? []),
          row.address?.city ?? "",
          row.address?.state ?? "",
        ];
        return parts.filter(Boolean).join(" ");
      },
      header: () => null,
      cell: () => null,
      enableHiding: true,
      enableSorting: false,
    },
    {
      accessorKey: "name",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="h-auto p-0 font-semibold"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Name
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <Link
          from="/dashboard/admin/agencies/"
          to="/dashboard/admin/agencies/$agencyId"
          params={{ agencyId: row.original.id }}
          className="text-primary font-medium hover:underline"
        >
          {row.original.name}
        </Link>
      ),
    },
    {
      id: "emails",
      header: "Emails",
      cell: ({ row }) => {
        const emails = row.original.emails ?? [];
        return emails.length ? emails[0] : "N/A";
      },
      enableSorting: false,
    },
    {
      id: "phone",
      header: "Phone",
      cell: ({ row }) => {
        const phones = row.original.phoneNumbers ?? [];
        return phones.length ? formatPhoneNumber(phones[0]) : "N/A";
      },
      enableSorting: false,
    },
    {
      id: "location",
      header: "Location",
      cell: ({ row }) => {
        const city = row.original.address?.city;
        const state = row.original.address?.state;
        return city && state ? `${city}, ${state}` : "N/A";
      },
      enableSorting: false,
    },
    {
      id: "createdAt",
      accessorFn: (row) => (row.createdAt ? ((row.createdAt as Timestamp).toMillis?.() ?? 0) : 0),
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="h-auto p-0 font-semibold"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Created
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => <span>{formatDate(row.original.createdAt)}</span>,
    },
    {
      id: "actions",
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <Link
          from="/dashboard/admin/agencies/"
          to="/dashboard/admin/agencies/$agencyId"
          params={{ agencyId: row.original.id }}
        >
            <Button variant="ghost" size="sm">
              <Eye className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      ),
      enableSorting: false,
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  if (agenciesResource.tag === "loading") {
    return <div className="container mx-auto p-6">Loading agencies...</div>;
  }

  if (agenciesResource.tag === "failure") {
    const { error: agenciesError } = agenciesResource;
    return (
      <div className="container mx-auto p-6">
        <Alert variant="destructive">
          <AlertDescription>Error loading agencies: {agenciesError.message}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto space-y-4 p-1 sm:p-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
        <h2 className="text-2xl font-bold tracking-tight">
          Agencies ({agenciesResource.asData?.length})
        </h2>

        <Link from="/dashboard/admin/agencies/" to="/dashboard/admin/agencies/create">
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Create Agency
          </Button>
        </Link>
      </div>

      <DataTable<AgencyModel, unknown>
        columns={columns}
        data={agenciesResource.asData ?? []}
        isLoading={false}
        emptyMessage="No agencies found"
        disablePagination={true}
        searchColumnId="all"
        initialVisibility={{ all: false }}
        searchValue={search}
        onSearchChange={handleSearchChange}
      />
    </div>
  );
}
